/**
 * The chosen skin.
 *
 * Kept in localStorage rather than on the account, and applied before React
 * mounts. Both halves of that matter.
 *
 * Per device, because the answer genuinely differs by device: the laptop in a
 * bright room and the phone at 1am are not asking for the same palette, and one
 * account-wide setting would force a single answer on both.
 *
 * Applied synchronously at boot because a skin fetched after render arrives as a
 * visible repaint — the app appearing in one palette and lurching into another a
 * beat later. On the screen someone opens when they feel bad, that flash is the
 * first thing the app does, and it reads as broken.
 */

export const SKINS = ['sky', 'default', 'soft', 'sketch', 'evening', 'crayon'] as const;
export type Skin = (typeof SKINS)[number];

const KEY = 'adhd-me:skin';
/*
 * SKY IS THE DEFAULT FROM 13 OCTOBER 2026 (release 1 of docs/PRD-2.0.md), and
 * it is shown once to a device that chose something else before that date.
 * Nick asked to wake up to 2.0; a device that had picked Evening in September
 * would otherwise never see it. Once this flag is set the choice is his again,
 * and Settings keeps every other look one tap away.
 */
const INTRODUCED = 'adhd-me:sky-introduced';

export function readSkin(): Skin {
  try {
    const v = localStorage.getItem(KEY);
    /*
     * Every skin, from one list.
     *
     * This tested two of them by name, so Evening could be chosen on the Theme
     * tab, applied instantly, and then silently revert to Standard on the next
     * reload — the setting appeared to work and did not survive. It was added
     * to the type, to the stylesheet and to the picker, and the one function
     * that decides whether a stored value is real was never told. A list, so
     * the next one cannot be half-added.
     */
    if (!localStorage.getItem(INTRODUCED)) {
      localStorage.setItem(INTRODUCED, '1');
      localStorage.setItem(KEY, 'sky');
      return 'sky';
    }
    return (SKINS as readonly string[]).includes(v ?? '') ? (v as Skin) : 'sky';
  } catch {
    // Private browsing, or storage disabled. A missing preference is not an
    // error worth surfacing — it just means the sky.
    return 'sky';
  }
}

export function applySkin(skin: Skin): void {
  const root = document.documentElement;
  // The attribute is REMOVED rather than set to "default". `data-skin` is a
  // separate axis from `data-theme`, which carries forced light/dark, and an
  // empty-but-present attribute is the kind of thing a future selector matches
  // by accident.
  if (skin !== 'default') root.setAttribute('data-skin', skin);
  else root.removeAttribute('data-skin');
  /*
   * The browser chrome follows the skin. index.html's theme-color is the
   * standard palette's --bg per scheme (check:tokens holds it to that); the
   * sky is dark in both schemes, so without this the address bar sat cream
   * above a near-black page.
   */
  for (const m of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (skin === 'sky') {
      if (!m.dataset.was) m.dataset.was = m.content;
      m.content = '#0F1317';
    } else if (m.dataset.was) {
      m.content = m.dataset.was;
      delete m.dataset.was;
    }
  }
}

export function saveSkin(skin: Skin): void {
  try {
    /* 'default' is stored by name now. It used to be stored as an absence,
       which was fine while absence meant the standard palette; it means the
       sky now, so a person who chose Standard has to be able to say so. */
    localStorage.setItem(KEY, skin);
  } catch {
    // Not fatal. The skin still applies for this session; it just will not be
    // remembered, which is better than refusing to change at all.
  }
  applySkin(skin);
}
